import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";

import { apiBase, RecorrerVistaDTO } from "./apiBase";
import { dinero, errorText, fechaCorta } from "./helpers";
import { BotonWhatsappAlumno } from "../../../../components/whatsapp/whatsappAlumno";

/*
	Recorrer Pagos (Actualizaciones Claras, Fase 2 — Esteban, 31-oct-2026): su siguiente mensualidad cae en el mismo mes que la misma
	mensualidad de su grupo nuevo; atrasarse N meses = N meses sin pago, el total no cambia. Dos lugares:
	- RecorrerEleccion: al aplicar un cambio de periodo (Por Aplicar y Una Alumna). Marcado si se atrasa; si va adelante, sin marcar.
	- RecorrerPagosVentana: «Recorrer Pagos» del Estado de Cuenta, para corregir lo ya aplicado (Supervisión de Caja).
*/

export const R = {
	recorrer: (n: number) => n > 0 ? `Recorrer Pagos ${n} ${n === 1 ? "Mes" : "Meses"}` : `Adelantar Pagos ${Math.abs(n)} ${Math.abs(n) === 1 ? "Mes" : "Meses"}`,
	dejar: "Dejar los Meses Como Están",
	meses: "Meses",
	titulo: "Recorrer Pagos",
	motivo: "Motivo",
	aplicar: "Recorrer Pagos",
	volver: "Volver",
	cerrar: "Cerrar",
	cargando: "Calculando…",
	sinNada: "No hay mensualidades por cursar que mover.",
	listo: "Pagos recorridos.",
	yaTomadas: "Ya Tomó Esas Clases",
	mas: (n: number) => `y ${n} más`
};

/** Las líneas de lo que pasaría con N meses (lo calcula el servidor con el plan de hoy). */
export const useVistaRecorrer = (campusId: number, enrollmentId: number | null | undefined, meses: number | null, toScheduleId?: number | null, activo: boolean = true) => {
	const [vista, setVista] = useState<RecorrerVistaDTO | null>(null);
	const [fallo, setFallo] = useState<string | null>(null);
	useEffect(() => {
		if (!enrollmentId || !activo) { setVista(null); return; }
		let vigente = true;
		// Sin vista vieja mientras llega la nueva (revisión 1-nov: al cambiar de destino se podía mandar la cuenta del anterior).
		setVista(null);
		setFallo(null);
		// Espera un respiro mientras se escribe el número de meses.
		const t = window.setTimeout(() => {
			apiBase.recorrerVista(campusId, enrollmentId, meses, toScheduleId).then((r) => vigente && setVista(r.data)).catch((e) => vigente && setFallo(errorText(e)));
		}, 250);
		return () => { vigente = false; window.clearTimeout(t); };
	}, [campusId, enrollmentId, meses, toScheduleId, activo]);
	return { vista, fallo };
};

const Lineas: React.FC<{ vista: RecorrerVistaDTO | null; fallo?: string | null }> = ({ vista, fallo }) => {
	if (fallo) { return <div className="wau-oro" style={{ fontSize: 13.5 }}>{fallo}</div>; }
	if (!vista) { return <div className="wau-sec" style={{ fontSize: 13.5 }}>{R.cargando}</div>; }
	return (
		<div className="wau-efectos">
			{vista.lineas.map((l, i) => <div key={"l" + i}>{l}</div>)}
			{vista.problemas.map((p, i) => <div key={"p" + i} className="wau-oro">{p}</div>)}
		</div>
	);
};

const Meses: React.FC<{ valor: number; onCambio: (n: number) => void; disabled?: boolean }> = ({ valor, onCambio, disabled }) => (
	<span className="wau-fila" style={{ gap: 6, alignItems: "center", flexWrap: "nowrap" }}>
		<button type="button" className="btn btn-outline-secondary btn-sm" disabled={disabled || valor <= -12} onClick={() => onCambio(valor - 1 === 0 ? -1 : valor - 1)} aria-label="Menos">−</button>
		<b className="wau-num" style={{ minWidth: 26, textAlign: "center" }}>{valor}</b>
		<button type="button" className="btn btn-outline-secondary btn-sm" disabled={disabled || valor >= 12} onClick={() => onCambio(valor + 1 === 0 ? 1 : valor + 1)} aria-label="Más">+</button>
		<span className="wau-sec small">{R.meses}</span>
	</span>
);

export interface EleccionRecorrer { on: boolean; meses: number; }

/**
 * La elección al aplicar un cambio de periodo. `sugerido` lo da el servidor; si va adelante (negativo) sale sin marcar y solo se puede
 * marcar si ninguna mensualidad sin pagar queda en el pasado.
 */
export const RecorrerEleccion: React.FC<{ campusId: number; enrollmentId: number; toScheduleId: number | null; sugerido: number; valor: EleccionRecorrer;
	onCambio: (v: EleccionRecorrer) => void; disabled?: boolean; soloDetalle?: boolean; onVista?: (v: RecorrerVistaDTO | null) => void }> = ({ campusId, enrollmentId, toScheduleId, sugerido, valor, onCambio, disabled, soloDetalle, onVista }) => {
	const { vista, fallo } = useVistaRecorrer(campusId, enrollmentId, valor.on ? valor.meses : sugerido, toScheduleId);
	useEffect(() => { if (onVista) { onVista(valor.on ? vista : null); } }, [vista, valor.on]);
	const bloqueado = !valor.on && sugerido < 0 && !!vista && vista.problemas.length > 0;
	if (soloDetalle) {
		return valor.on ? <div style={{ marginTop: 8 }}><Meses valor={valor.meses} onCambio={(n) => onCambio({ on: true, meses: n })} disabled={disabled} /><div style={{ marginTop: 6 }}><Lineas vista={vista} fallo={fallo} /></div></div> : null;
	}
	return (
		<div className="wau-pila" style={{ gap: 8 }}>
			<label className={"wau-opcion" + (valor.on ? " on" : "")} style={bloqueado ? { opacity: .55 } : undefined}>
				<input type="checkbox" checked={valor.on} disabled={disabled || bloqueado} onChange={(e) => onCambio({ on: e.target.checked, meses: valor.meses || sugerido })} />
				<span className="t">{R.recorrer(valor.on ? valor.meses : sugerido)}</span>
				<span className="d">{bloqueado ? (vista?.problemas[0] || "") : (vista?.lineas.slice(0, 2).join(" ") || R.cargando)}</span>
			</label>
			{valor.on ? <div style={{ paddingLeft: 30 }}><Meses valor={valor.meses} onCambio={(n) => onCambio({ on: true, meses: n })} disabled={disabled} />
				<div style={{ marginTop: 6 }}><Lineas vista={vista} fallo={fallo} /></div></div> : null}
		</div>
	);
};

/** «Recorrer Pagos» del Estado de Cuenta: corrige una matrícula que ya cambió de periodo (Tefany #287, Cheily #673). */
export const RecorrerPagosVentana: React.FC<{ campusId: number; enrollmentId: number; studentId: number; studentName: string; courseName?: string | null; countryId?: number | null;
	onClose: () => void; onDone: () => void }> = ({ campusId, enrollmentId, studentId, studentName, courseName, countryId, onClose, onDone }) => {
	const [meses, setMeses] = useState<number | null>(null);
	const [motivo, setMotivo] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [hecho, setHecho] = useState<RecorrerVistaDTO | null>(null);
	const { vista, fallo } = useVistaRecorrer(campusId, enrollmentId, meses);
	useEffect(() => { if (vista && meses === null) { setMeses(vista.sugerido || 1); } }, [vista, meses]);
	const n = meses ?? 0;
	const puede = !!vista && vista.canApply && n !== 0 && vista.filas.length > 0 && vista.problemas.length === 0 && motivo.trim().length >= 10 && !busy;
	const aplicar = () => {
		setBusy(true); setError(null);
		apiBase.recorrerAplicar(campusId, enrollmentId, n, motivo.trim()).then((r) => { setHecho(r.data); onDone(); }).catch((e) => setError(errorText(e))).finally(() => setBusy(false));
	};
	return (
		<Modal show onHide={onClose} centered>
			<Modal.Header closeButton><Modal.Title as="h5">{R.titulo}</Modal.Title></Modal.Header>
			<Modal.Body>
				<div style={{ fontSize: 14, marginBottom: 8 }}><b>{studentName}</b>{courseName ? <span className="wau-sec">, {courseName}</span> : null}</div>
				{hecho ? (
					<>
						<div className="alert alert-light border" style={{ fontSize: 14 }}>{R.listo} {hecho.lineas.slice(1, 2).join(" ")}</div>
						{hecho.whatsappText ? <div className="waAviso" style={{ whiteSpace: "pre-wrap" }}>{hecho.whatsappText}
							<div style={{ marginTop: 6 }}><BotonWhatsappAlumno campusId={campusId} studentId={studentId} nombre={studentName} texto sugerido={hecho.whatsappText} /></div></div> : null}
					</>
				) : (
					<>
						{vista && vista.filas.length === 0 ? <div className="wau-sec">{R.sinNada}</div> : (
							<>
								<Meses valor={n} onCambio={setMeses} disabled={busy || !vista} />
								<div style={{ margin: "10px 0" }}><Lineas vista={vista} fallo={fallo} /></div>
								{vista && vista.filas.length > 0 ? (
									<table className="table table-sm mb-2" style={{ fontSize: 13.5 }}>
										<tbody>
											{vista.filas.slice(0, 4).map((f) => (
												<tr key={"f" + f.id}>
													<td>{f.titulo}{f.pagada ? <span className="wau-sec">, pagada</span> : null}</td>
													<td style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}><span className="wau-sec">{fechaCorta(f.antes)}</span> → <b>{fechaCorta(f.despues)}</b></td>
													<td style={{ textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{dinero(f.monto, countryId)}</td>
												</tr>
											))}
											{vista.filas.length > 4 ? <tr><td colSpan={3} className="wau-sec">{R.mas(vista.filas.length - 4)}</td></tr> : null}
										</tbody>
									</table>
								) : null}
								<label className="wau-campo"><span className="wau-lab">{R.motivo}</span>
									<textarea className="wau-entrada" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} /></label>
							</>
						)}
						{error ? <div className="wau-oro" style={{ fontSize: 13.5, marginTop: 8 }}>{error}</div> : null}
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				{hecho ? <button type="button" className="btn btn-primary btn-sm rounded-pill" onClick={onClose}>{R.cerrar}</button> : (
					<>
						<button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={busy}>{R.volver}</button>
						<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={!puede} onClick={aplicar}>{R.aplicar}</button>
					</>
				)}
			</Modal.Footer>
		</Modal>
	);
};
